import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import type { MessageInformDto } from "@/api/types";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useTranslation } from "@/translations";

import { User } from "../User/User";

type InformedAdminsModalProps = ModalBaseProps & {
  messageId: string;
};

export function InformedAdminsModal({ messageId, isOpened, onOpenChange }: InformedAdminsModalProps): React.ReactNode {
  const { t, i18n } = useTranslation();
  const communityFeedQueries = useCommunityFeedQueries();

  const {
    data: informedAdminsData,
    isPending: isPendingInformedAdmins,
    hasNextPage: hasMoreInformedAdmins,
    isFetchingNextPage: isFetchingMoreInformedAdmins,
    fetchNextPage: fetchMoreInformedAdmins,
    error: informedAdminsError,
  } = useInfiniteQuery({
    ...communityFeedQueries.informedAdminsInfinite(messageId),
    enabled: isOpened,
  });

  const informedAdminsModal = useMemo(
    () => informedAdminsData?.pages.flatMap((x) => x.items ?? []) ?? [],
    [informedAdminsData],
  );

  const loaderRef = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreInformedAdmins && hasMoreInformedAdmins) {
        void fetchMoreInformedAdmins();
      }
    }, [fetchMoreInformedAdmins, hasMoreInformedAdmins, isFetchingMoreInformedAdmins]),
  });

  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<MessageInformDto>();

    return [
      helper.accessor("informed", {
        header: t("component.community-post.informed-admins.modal.header.informed"),
        cell: (cell) => <User size="sm" user={cell.getValue()} />,
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("informedBy", {
        header: t("component.community-post.informed-admins.modal.header.by"),
        cell: (cell) => <User size="sm" user={cell.getValue()} />,
        minSize: TABLE_COLUMN_SIZES.USER.MIN,
        size: TABLE_COLUMN_SIZES.USER.DEFAULT,
      }),
      helper.accessor("createdAt", {
        header: t("component.community-post.informed-admins.modal.header.on"),
        cell: (cell) => (
          <span className="pr-space-md whitespace-nowrap">{formatDate(i18n, "date", cell.getValue())}</span>
        ),
        minSize: TABLE_COLUMN_SIZES.DATE.MIN,
        size: TABLE_COLUMN_SIZES.DATE.DEFAULT,
      }),
    ];
  }, [t, i18n]);

  const tableInstance = useResizableTable(TABLE_KEYS.COMMUNITY_INFORMED_ADMINS, {
    columns,
    data: informedAdminsModal ?? [],
    enableSorting: false,
  });

  return (
    <Modal.Root
      title={t("component.community-post.informed-admins.modal.title")}
      description={t("component.community-post.informed-admins.modal.description")}
      height={680}
      size="lg"
      isPending={isPendingInformedAdmins}
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Stack gap="md" data-testid="informed-admins-modal-content">
          {(informedAdminsError || !informedAdminsModal.length) && (
            <span className="text-caption">{t("component.community-post.informed-admins.modal.no-data")}</span>
          )}
          {informedAdminsModal.length > 0 && (
            <section className="flex size-full flex-col items-center">
              <Table table={tableInstance} data-testid="informed-admins-list" />
              {hasMoreInformedAdmins && (
                <div className="p-space-md" ref={loaderRef}>
                  <Box padding={{ y: "md" }}>
                    <Inline justify="center">
                      <Spinner size="md" />
                    </Inline>
                  </Box>
                </div>
              )}
            </section>
          )}
        </Stack>
      </Modal.Body>
      <Modal.Actions isSticky>
        <Modal.Close>
          <Button variant="filled">{t("common.action.close")}</Button>
        </Modal.Close>
      </Modal.Actions>
    </Modal.Root>
  );
}
